import { fmtBigInteger } from "@utils/format";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import * as d3 from "d3";

export type KeyValuePair = { key: string; value: number; color?: string };

type ComponentProps = {
  id?: string;
  data: KeyValuePair[];
};

function BarChart(props: ComponentProps & SizeProps) {
  const margin = { top: 10, right: 0, bottom: 50, left: 40 };
  /**
   * Returns the width, height of the actual chart drawing area (total SVG size minus margins)
   */
  const chartSize = {
    chartWidth: props.size.width - margin.left - margin.right,
    chartHeight: props.size.height - margin.top - margin.bottom,
  };
  /**
   * Computes the linear scales for the x and y dimension for the given data and chart sizes
   */
  function getScales(data: KeyValuePair[], chartWidth: number, chartHeight: number) {
    const { keys, valueRange } = computeDataRange(data);

    return {
      xScale: d3.scaleBand().domain(keys).range([0, chartWidth]).padding(0.2),
      yScale: d3.scaleLinear().domain(valueRange).range([chartHeight, 0]).nice(),
    };
  }

  /**
   * Returns the data ranges (min/max) for the x and y dimension
   */
  function computeDataRange(data: KeyValuePair[]): {
    keys: string[];
    valueRange: number[];
  } {
    const max = data.reduce((max, { value }) => (value > max ? value : max), 0);
    return {
      keys: data.map(({ key }) => key),
      valueRange: [0, max], // Always set 0 as min value
    };
  }
  const getChartSize = () => chartSize;
  const { chartWidth, chartHeight } = getChartSize();

  const { xScale, yScale } = getScales(props.data, chartWidth, chartHeight);

  return (
    <svg width={props.size.width} height={props.size.height} xmlns="http://www.w3.org/2000/svg" version="1.1">
      <g transform={`translate(${margin.left}, ${margin.top})`}>
        {props.data.map(({ key, value, color }, ix) => {
          return (
            <rect
              key={ix}
              fill={color}
              x={xScale(key)}
              y={yScale(value)}
              width={xScale.bandwidth()}
              height={chartHeight - yScale(value)}
            />
          );
        })}
        <g transform={`translate(0, ${chartHeight})`}>
          <path d={`M 0 0 h ${chartWidth}`} stroke="currentColor" />
          {props.data.map(({ key }, ix) => {
            const offset = xScale.paddingOuter() + ix * xScale.step() + xScale.bandwidth() / 2;
            return (
              <g key={`t-${ix}`} transform={`translate(${offset}, 0)`}>
                <text
                  style={{
                    fontSize: "10px",
                    textAnchor: "end",
                    transform: "translate(6px, 8px) rotate(-45deg)",
                    fill: "currentColor",
                  }}
                >
                  {key}
                </text>
              </g>
            );
          })}
        </g>
      </g>
      <g transform={`translate(0, ${margin.top})`}>
        <path d={`M ${margin.left} 0 V ${chartHeight}`} stroke="currentColor" />

        {yScale.ticks(Math.ceil(chartHeight / 50)).map((value, ix) => {
          return (
            <g key={ix} transform={`translate(0,${yScale(value)})`}>
              <path d={`M ${margin.left - 5} 0 h 5`} stroke="currentColor" />
              <text
                style={{
                  fontSize: "10px",
                  textAnchor: "end",
                  transform: `translate(${margin.left - 10}px, 3px)`,
                  fill: "currentColor",
                }}
              >
                {fmtBigInteger(value)}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export default ResizeComponent(BarChart);
